import React, { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useOutsideReferral } from "@/hooks/followup/useOutsideReferral";
import { useAutoSaveInit } from "@/hooks/useAutoSaveInit";
import { useSpecialists } from "@/hooks/followup/useSpecialists";
import { useDebounce } from "@/hooks/use-debounce";
import {
  Widget,
  WidgetHeader,
  WidgetContent,
} from "@/components/consultation/shared/Widget";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const MAX_INSTRUCTIONS = 45;
const SPECIALIST_NONE = "__none__";

interface OutsideReferralCardProps {
  appointmentId: string;
  isCompleted?: boolean;
  parentType?: "prescription" | "meal_plan";
}

export const OutsideReferralCard = ({
  appointmentId,
  isCompleted = false,
  parentType = "prescription",
}: OutsideReferralCardProps) => {
  const {
    data: referral,
    create,
    update,
    isCreating,
    isUpdating,
  } = useOutsideReferral(appointmentId, parentType);
  const { data: specialists, isLoading: isLoadingSpecialists } =
    useSpecialists();
  const { isInitialized, markInitialized } = useAutoSaveInit([
    appointmentId,
    parentType,
  ]);

  const [specialistId, setSpecialistId] = useState<string>("");
  const [instructions, setInstructions] = useState<string>("");

  useEffect(() => {
    if (referral && !isInitialized.current) {
      setSpecialistId(referral.specialist || "");
      setInstructions(referral.instructions || "");
      markInitialized();
    }
  }, [referral, isInitialized, markInitialized]);

  const debouncedInstructions = useDebounce(instructions, 1000);

  useEffect(() => {
    if (!referral && !specialistId) return;
    if (referral && !isInitialized.current) return;

    if (
      referral &&
      referral.specialist === specialistId &&
      referral.instructions === debouncedInstructions
    ) {
      return;
    }

    if (!referral && !specialistId) return;

    const payload = {
      specialist: specialistId,
      instructions: debouncedInstructions,
    };

    if (referral) {
      update({ id: referral.id, data: payload });
    } else {
      create({ parent_type: parentType, ...payload });
    }
  }, [specialistId, debouncedInstructions, referral, create, update]);

  const isSaving = isCreating || isUpdating;
  const isReasonRequired = !!specialistId;
  const isReasonInvalid = isReasonRequired && instructions.trim() === "";
  const remaining = MAX_INSTRUCTIONS - instructions.length;

  return (
    <Widget>
      <WidgetHeader
        title="Outside Referral"
        hasInfo
        extras={
          isSaving ? (
            <Loader2 className="size-3 animate-spin text-muted-foreground" />
          ) : undefined
        }
      />
      <WidgetContent className="pt-3">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
          <div className="space-y-1.5 lg:col-span-4">
            <label className="z-label text-muted-foreground">Specialist</label>
            <Select
              value={specialistId}
              onValueChange={(val) =>
                setSpecialistId(val === SPECIALIST_NONE ? "" : val)
              }
              disabled={isCompleted}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    isLoadingSpecialists ? "Loading..." : "Select specialist"
                  }
                />
              </SelectTrigger>
              <SelectContent position="popper" className="max-h-40">
                <SelectItem value={SPECIALIST_NONE}>
                  <span className="text-muted-foreground">None</span>
                </SelectItem>
                {specialists?.map((specialist) => (
                  <SelectItem key={specialist.id} value={specialist.id}>
                    {specialist.category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5 lg:col-span-8">
            <label className="z-label text-muted-foreground">
              Reason for referring
              {isReasonRequired && (
                <span className="text-destructive ml-0.5">*</span>
              )}
            </label>
            <Input
              type="text"
              placeholder="e.g. For your knee pain"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              disabled={isCompleted || !specialistId}
              maxLength={MAX_INSTRUCTIONS}
              aria-invalid={isReasonInvalid}
            />
            <p
              className={cn(
                "z-hint text-right",
                remaining <= 10 ? "text-destructive" : "text-muted-foreground"
              )}
            >
              {remaining} left
            </p>
          </div>
        </div>
      </WidgetContent>
    </Widget>
  );
};
